import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProgressIndicator } from './ProgressIndicator';
import { Step1BasicInfo } from './Step1BasicInfo';
import { Step2PricingTier } from './Step2PricingTier';
import { Step3Features } from './Step3Features';
import { Step4Quotas } from './Step4Quotas';
import { Step5Review } from './Step5Review';
import { useCreatePlatform } from '../../hooks/usePlatforms';

interface FormData {
  name: string;
  slug: string;
  description: string;
  pricingTier: string;
  selectedFeatures: string[];
  quotas: {
    maxTransactions: number;
    maxApiCalls: number;
    maxUsers: number;
  };
}

const STEPS = [
  { number: 1, label: 'Basic Information', shortLabel: 'Basic Info' },
  { number: 2, label: 'Pricing Tier', shortLabel: 'Pricing' },
  { number: 3, label: 'Feature Selection', shortLabel: 'Features' },
  { number: 4, label: 'Custom Quotas', shortLabel: 'Quotas' },
  { number: 5, label: 'Review', shortLabel: 'Review' },
];

const DEFAULT_QUOTAS = {
  starter: { maxTransactions: 5000, maxApiCalls: 50000, maxUsers: 25 },
  growth: { maxTransactions: 10000, maxApiCalls: 100000, maxUsers: 50 },
  enterprise: { maxTransactions: 1000000, maxApiCalls: 10000000, maxUsers: 1000 },
};

export function PlatformWizard() {
  const navigate = useNavigate();
  const createPlatform = useCreatePlatform();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    slug: '',
    description: '',
    pricingTier: 'growth',
    selectedFeatures: ['payments', 'ach_transfers', 'digital_wallets', 'fraud_detection', 'payouts'],
    quotas: DEFAULT_QUOTAS.growth,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateFormData = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const updatePricingTier = (tier: string) => {
    updateFormData('pricingTier', tier);
    // Update quotas to match new tier defaults
    updateFormData('quotas', DEFAULT_QUOTAS[tier as keyof typeof DEFAULT_QUOTAS] || DEFAULT_QUOTAS.starter);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          newErrors.name = 'Platform name is required';
        } else if (formData.name.length < 3) {
          newErrors.name = 'Platform name must be at least 3 characters';
        } else if (formData.name.length > 50) {
          newErrors.name = 'Platform name must be less than 50 characters';
        }

        if (!formData.slug.trim()) {
          newErrors.slug = 'Platform slug is required';
        } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
          newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
        } else if (formData.slug.length < 3) {
          newErrors.slug = 'Slug must be at least 3 characters';
        } else if (formData.slug.length > 30) {
          newErrors.slug = 'Slug must be less than 30 characters';
        }
        break;

      case 2:
        if (!formData.pricingTier) {
          newErrors.pricingTier = 'Please select a pricing tier';
        }
        break;

      case 3:
        // Features are optional, no validation needed
        break;

      case 4:
        // Quotas validation
        if (formData.quotas.maxTransactions < 100) {
          newErrors.maxTransactions = 'Minimum 100 transactions';
        }
        if (formData.quotas.maxApiCalls < 1000) {
          newErrors.maxApiCalls = 'Minimum 1,000 API calls';
        }
        if (formData.quotas.maxUsers < 1) {
          newErrors.maxUsers = 'Minimum 1 user';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleEdit = (step: number) => {
    setCurrentStep(step);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    try {
      const platform = await createPlatform.mutateAsync({
        metaPlatformId: 'mplat_1761106422341_ozhlhugmp', // AppEdge meta-platform ID
        name: formData.name,
        slug: formData.slug,
        description: formData.description || undefined,
        pricingTier: formData.pricingTier,
        enabledFeatures: formData.selectedFeatures,
        customQuotas: {
          maxTransactions: formData.quotas.maxTransactions,
          maxApiCalls: formData.quotas.maxApiCalls,
          maxUsers: formData.quotas.maxUsers,
        },
      });

      // Success! Navigate to the new platform or platforms list
      if (platform?.id) {
        navigate(`/platforms/${platform.id}`);
      } else {
        navigate('/platforms');
      }
    } catch (error) {
      console.error('Failed to create platform:', error);
      // Error is handled by react-query and will show in UI
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1BasicInfo
            formData={{
              name: formData.name,
              slug: formData.slug,
              description: formData.description,
            }}
            errors={errors}
            onChange={updateFormData}
          />
        );
      case 2:
        return (
          <Step2PricingTier
            selectedTier={formData.pricingTier}
            onChange={updatePricingTier}
          />
        );
      case 3:
        return (
          <Step3Features
            selectedFeatures={formData.selectedFeatures}
            onChange={(features) => updateFormData('selectedFeatures', features)}
          />
        );
      case 4:
        return (
          <Step4Quotas
            pricingTier={formData.pricingTier}
            quotas={formData.quotas}
            onChange={(quotas) => updateFormData('quotas', quotas)}
          />
        );
      case 5:
        return <Step5Review data={formData} onEdit={handleEdit} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress indicator */}
      <ProgressIndicator
        currentStep={currentStep}
        totalSteps={STEPS.length}
        steps={STEPS}
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Step content */}
          <div className="p-8">{renderStep()}</div>

          {/* Footer with navigation buttons */}
          <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 flex items-center justify-between">
            <div>
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200"
                >
                  Back
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/platforms')}
                className="px-4 py-2.5 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors duration-200"
              >
                Cancel
              </button>

              {currentStep < STEPS.length ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={currentStep === 2 && !formData.pricingTier}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={createPlatform.isPending}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-md disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
                >
                  {createPlatform.isPending ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Creating...
                    </>
                  ) : (
                    'Create Platform'
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Error message */}
          {createPlatform.isError && (
            <div className="px-8 pb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg
                    className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="text-sm text-red-900">
                    <strong>Failed to create platform</strong>
                    <p className="mt-1 text-red-800">
                      {createPlatform.error instanceof Error
                        ? createPlatform.error.message
                        : 'An unexpected error occurred. Please try again.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
